import { useEffect, useReducer, useRef, useState } from "react";
import { auth } from "./firebase";
import { resolveShift } from "./utils/shiftScope";
import UnscopedScreenBadge from "./components/overlay/UnscopedScreenBadge";
import { onAuthStateChanged, getRedirectResult } from "firebase/auth";
import { db } from "./firebase";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import Login from "./Login";
import RegisterView from "./RegisterView";
import CustomerOrderView from "./CustomerOrderView";
import ArtistQueueView from "./ArtistQueueView";
import CameraView from "./CameraView";
import LibraryView from "./pages/LibraryView";
import SocialHub from "./SocialHub";
import CaptureStory from "./social/CaptureStory";
import InsightsView from "./InsightsView";
import HelpView from "./HelpView";
import LabView from "./pages/lab/LabView";
import OverlayMain from "./pages/overlay/Main";
import OverlayAlerts from "./pages/overlay/Alerts";
import OverlayStartingSoon from "./pages/overlay/StartingSoon";
import OverlayBreak from "./pages/overlay/Break";
import OverlayFaceCam from "./pages/overlay/FaceCam";
import OverlayScreenShare from "./pages/overlay/ScreenShare";
import OverlayStinger from "./pages/overlay/Stinger";
import OverlayTitleCard from "./pages/overlay/TitleCard";
import OverlayTitleCardGTS from "./pages/overlay/TitleCardGTS";
import OverlayTitleCardStat from "./pages/overlay/TitleCardStat";
import OverlayTitleCardEnd from "./pages/overlay/TitleCardEnd";
import OverlayReference from "./pages/overlay/Reference";
import OverlayOutro from "./pages/overlay/Outro";
import OverlayBooth from "./pages/overlay/Booth";
import LivestreamJoinView from "./LivestreamJoinView";
import MeditationsView from "./pages/MeditationsView";
import MeditationDetail from "./pages/site/MeditationDetail";
import MeditationsArchive from "./pages/site/MeditationsArchive";
import ShiftMeditationDetail from "./pages/site/ShiftMeditationDetail";
import EventsPage from "./pages/site/EventsPage";
import EventDetail from "./pages/site/EventDetail";
import RecapPage from "./pages/site/RecapPage";
import ShippingPolicy from "./pages/site/ShippingPolicy";
import PrivacyPolicy from "./pages/site/PrivacyPolicy";
import TermsOfService from "./pages/site/TermsOfService";
import TestimonialsModeration from "./pages/TestimonialsModeration";
import KeepsakePreview from "./pages/KeepsakePreview";
import ReviewPage from "./pages/ReviewPage";
import BookingsList from "./pages/crm/BookingsList";
import BookingDetail from "./pages/crm/BookingDetail";
import ClientDetail from "./pages/crm/ClientDetail";
import QuickAddFlow from "./pages/crm/QuickAddFlow";
import PublicEventQuickAdd from "./pages/crm/PublicEventQuickAdd";
import ContractHelper from "./pages/crm/ContractHelper";
import FlashBoardSubmit from "./pages/crm/FlashBoardSubmit";
import SiteHome from "./pages/site/Home";
import SiteStub from "./pages/site/StubPage";
import QuotePage from "./pages/site/QuotePage";
import ShopPage from "./pages/site/ShopPage";
import ShopThanks from "./pages/site/ShopThanks";
import ShopCheckout from "./pages/site/ShopCheckout";
import OrdersFulfillmentView from "./pages/shop/OrdersFulfillmentView";
import CampaignsList from "./pages/campaigns/CampaignsList";
import CampaignEditor from "./pages/campaigns/CampaignEditor";
import StreamSetup from "./pages/config/StreamSetup";
import OverlayPreviews from "./pages/config/OverlayPreviews";
import EventConfig from "./pages/config/EventConfig";
import StaffCalendar from "./pages/calendar/StaffCalendar";
import FixLooseEnds from "./pages/looseEnds/FixLooseEnds";
import NewGig from "./pages/looseEnds/NewGig";
import CommissionUpload from "./pages/CommissionUpload";
import PreUploadForm from "./pages/PreUploadForm";
import StudioQueue from "./pages/StudioQueue";
import PracticeView from "./pages/PracticeView";
import IdentityBadge from "./components/IdentityBadge";
import GalleryPage from "./pages/site/GalleryPage";
import PressKit from "./pages/site/PressKit";
import { STAFF_THEME as T, FONT } from "./theme/staff";
import { trackPageView } from "./utils/analytics";

function appReducer(state, action) {
  switch (action.type) {
    case "AUTH_RESOLVED": return { ...state, user: action.user };
    case "NAVIGATE": return { ...state, path: action.path };
    default: return state;
  }
}

const initialState = {
  user: undefined,
  path: window.location.pathname,
};

const STAFF_EMAIL = "jump@thejoyofpancakes.com";

function playNewOrderSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.2].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0, ctx.currentTime + delay);
      gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + delay + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.18);
      osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.2);
    });
  } catch { /* ignore */ }
}

function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user, path } = state;
  const [staffOnly, setStaffOnly] = useState(false);
  // Scoped role for non-admin logins: { role: "admin" | "artist", displayName }.
  // Resolved from staffRoles/{email} at sign-in; admin account is implicit.
  const [roleInfo, setRoleInfo] = useState(null);
  const prevOrderCountRef = useRef(null);
  const orderUnsubRef     = useRef(null);

  // Global new-order sound — fires on any staff view when a new order lands.
  useEffect(() => {
    if (!user) {
      prevOrderCountRef.current = null;
      if (orderUnsubRef.current) { orderUnsubRef.current(); orderUnsubRef.current = null; }
      return;
    }
    const shiftUnsub = onSnapshot(
      query(collection(db, "shifts"), where("isActive", "==", true)),
      (snap) => {
        if (orderUnsubRef.current) { orderUnsubRef.current(); orderUnsubRef.current = null; }
        prevOrderCountRef.current = null;
        if (snap.empty) return;
        // Chime only for THIS device's queue (2026-09-25). Unscoped, a device
        // at one venue would ding for every order landing at the other — the
        // fastest way to train everyone to ignore the sound that matters.
        const scoped = resolveShift(snap.docs.map(d => ({ id: d.id, ...d.data() }))).shift;
        if (!scoped) return;
        const shiftId = scoped.id;
        orderUnsubRef.current = onSnapshot(
          query(
            collection(db, "orders"),
            where("shiftId", "==", shiftId),
            where("state", "in", ["queued", "drawing", "cooking", "flipping", "suspended", "ready_to_deliver"])
          ),
          (orderSnap) => {
            const count = orderSnap.docs.filter(d => !d.data().trashedAt).length;
            if (prevOrderCountRef.current === null) { prevOrderCountRef.current = count; return; }
            if (count > prevOrderCountRef.current) playNewOrderSound();
            prevOrderCountRef.current = count;
          }
        );
      }
    );
    return () => {
      shiftUnsub();
      if (orderUnsubRef.current) { orderUnsubRef.current(); orderUnsubRef.current = null; }
    };
  }, [user]);

  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      console.error("Redirect sign-in error:", err);
    });
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (u && !u.isAnonymous && u.email !== STAFF_EMAIL) {
        // Not the admin account — check staffRoles/{email} for a scoped role
        // (e.g. a practicing artist). No role doc → same rejection as before.
        // While the lookup runs, `user` stays undefined → Loading screen.
        getDoc(doc(db, "staffRoles", u.email.toLowerCase()))
          .then((snap) => {
            if (snap.exists() && snap.data().role === "artist") {
              setStaffOnly(false);
              setRoleInfo({
                role: "artist",
                artistId: snap.data().artistId || null,
                displayName: snap.data().displayName || u.displayName || u.email.split("@")[0],
              });
              dispatch({ type: "AUTH_RESOLVED", user: u });
            } else {
              auth.signOut();
              setStaffOnly(true);
              setRoleInfo(null);
              dispatch({ type: "AUTH_RESOLVED", user: null });
            }
          })
          .catch(() => {
            auth.signOut();
            setStaffOnly(true);
            setRoleInfo(null);
            dispatch({ type: "AUTH_RESOLVED", user: null });
          });
        return;
      }
      setStaffOnly(false);
      setRoleInfo(u && !u.isAnonymous ? { role: "admin", displayName: u.displayName || "Dan" } : null);
      // Anonymous sessions (overlay/public flows) must not unlock staff
      // routes — treat them as signed-out so staff land on the Login screen
      // instead of hitting silent Firestore permission errors on every write.
      dispatch({ type: "AUTH_RESOLVED", user: u && !u.isAnonymous ? u : null });
    });
    return unsubscribe;
  }, []);

  const navigate = (to) => {
    window.history.pushState(null, "", to);
    // Path state drives route matching — strip query string so navigate("/x?a=1")
    // still matches `if (path === "/x")`. Consumers that need query params read
    // window.location.search directly (LibraryView, CustomerOrderView, PromoPreview).
    dispatch({ type: "NAVIGATE", path: to.split("?")[0] });
    trackPageView(to);
  };

  // Browser back/forward: for PUBLIC routes, force a full page reload so
  // decoded images + SDK state from the previous page are fully released.
  // SPA-navigating back from /events to /site would keep both pages' images
  // in memory; a reload starts fresh. Staff routes stay SPA (keeps auth +
  // real-time listeners alive).
  const PUBLIC_ROUTE_PREFIXES = ["/events", "/gallery", "/shop", "/watch", "/quote", "/m", "/shipping", "/privacy", "/terms", "/commission", "/press", "/media", "/presskit"];
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname;
      const isPublic = p === "/" || PUBLIC_ROUTE_PREFIXES.some(pfx => p === pfx || p.startsWith(pfx + "/"));
      if (isPublic) {
        // Full reload — clean memory, fresh start
        window.location.reload();
      } else {
        dispatch({ type: "NAVIGATE", path: p });
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Client pre-upload portal — public, bearer-token via ?t=. Must precede
  // the /order catchall below (which also matches ?t= URLs).
  if (path === "/upload") {
    return <PreUploadForm />;
  }

  // Customer order page needs no auth — render immediately, before auth resolves.
  // Without this, customers see a blank loading screen while Firebase Auth initialises
  // on their own network (cellular), which can take several seconds or hang entirely.
  // The ?t= catchall handles legacy QR URLs that landed on / with just a query string.
  if (path === "/order" || (path === "/" && window.location.search.includes("?t="))) {
    return <CustomerOrderView />;
  }

  // Flash board client upload — public, no auth required. Path: /flash/{token}
  if (path.startsWith("/flash/")) {
    const token = path.slice("/flash/".length).split("/")[0];
    if (token) return <FlashBoardSubmit token={token} />;
  }

  // Public marketing site — no auth. `/` is the canonical home; `/site` kept
  // as a redirect for backward compatibility and internal bookmarks.
  if (path === "/" || path === "/site" || path.startsWith("/site/")) {
    return <SiteHome navigate={navigate} />;
  }

  // Public meditations blog — /m is the archive index; /m/{orderId} is a
  // single post. No auth. Lives in the public section so anonymous visitors
  // can read without hitting the auth gate below.
  if (path === "/m" || path === "/m/") {
    return <MeditationsArchive navigate={navigate} />;
  }
  // Shift meditation permalink — must precede the single-segment /m/{orderId}
  // branch below (which would otherwise treat "shift" as an order id).
  if (path.startsWith("/m/shift/")) {
    const id = path.slice("/m/shift/".length).split("/")[0];
    if (id) return <ShiftMeditationDetail shiftId={id} navigate={navigate} />;
    return <MeditationsArchive navigate={navigate} />;
  }
  if (path.startsWith("/m/") && !path.startsWith("/menu")) {
    const id = path.slice("/m/".length).split("/")[0];
    return <MeditationDetail orderId={id} navigate={navigate} />;
  }

  // Public booking form — ported from the legacy Google-Forms flow. Posts
  // to bookingFormSubmit Cloud Function (via /api rewrite) which writes to
  // the CRM's bookings collection.
  if (path === "/quote") {
    return <QuotePage navigate={navigate} />;
  }

  // Public site stub routes — nav links resolve here until each gets its own page.
  if (path === "/events") {
    return <EventsPage />;
  }
  if (path.startsWith("/events/")) {
    const id = path.slice("/events/".length).split("/")[0];
    if (id) return <EventDetail bookingId={id} />;
    return <EventsPage />;
  }
  // Public per-shift recap — shareable post-event gallery
  if (path.startsWith("/recap/")) {
    const id = path.slice("/recap/".length).split("/")[0];
    if (id) return <RecapPage shiftId={id} />;
  }
  // Headless-capture target for the animated Story mp4 (public; reads only the
  // PII-safe publicShiftRecaps doc). Rendered full-bleed 1080x1920, no chrome.
  if (path === "/social/capture") {
    return <CaptureStory />;
  }
  // Public pancake gallery — Pancakeopedia v1
  if (path === "/gallery") {
    return <GalleryPage />;
  }
  if (path.startsWith("/gallery/")) {
    const id = path.slice("/gallery/".length).split("/")[0];
    return <GalleryPage initialId={id} />;
  }

  // Press kit — destination for the QR code on the printed sell sheet.
  // /press is canonical; /media and /presskit are aliases that rewrite the
  // URL so the QR target stays the single citable address.
  if (path === "/press") {
    return <PressKit />;
  }
  if (path === "/media" || path === "/presskit") {
    window.history.replaceState(null, "", "/press");
    return <PressKit />;
  }

  if (path === "/shipping" || path === "/shipping-returns") {
    return <ShippingPolicy />;
  }
  if (path === "/privacy") {
    return <PrivacyPolicy />;
  }
  if (path === "/terms") {
    return <TermsOfService />;
  }
  if (path === "/watch") {
    return <SiteStub
      title="Watch Live"
      blurb="When Dan's at the griddle, you can watch the artistry happen in real time. Twitch + YouTube embeds will live here."
    />;
  }
  if (path === "/shop") {
    return <ShopPage navigate={navigate} />;
  }
  if (path === "/shop/checkout") {
    return <ShopCheckout navigate={navigate} />;
  }
  if (path === "/shop/thanks") {
    return <ShopThanks navigate={navigate} />;
  }
  // Commission upload wizard — public (post-purchase) + staff preview
  if (path === "/commission/upload") {
    return <CommissionUpload />;
  }
  if (path === "/commission/preview") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return <CommissionUpload preview />;
  }

  // Staff-only fulfillment view for paid shop orders (mirrored from Square)
  if (path === "/shop/orders") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return <OrdersFulfillmentView navigate={navigate} />;
  }

  // Every /overlay/* scene renders behind the same diagnostic: with two
  // queues live, a browser source whose URL carries no ?s=CODE cannot resolve
  // a shift and would otherwise go quietly blank on a public screen. One
  // wrapper here beats editing fourteen scenes.
  if (path.startsWith("/overlay/")) {
    const scene =
        path === "/overlay/main"           ? <OverlayMain />
      : path === "/overlay/alerts"         ? <OverlayAlerts />
      : path === "/overlay/starting-soon"  ? <OverlayStartingSoon />
      : path === "/overlay/break"          ? <OverlayBreak />
      : path === "/overlay/face-cam"       ? <OverlayFaceCam />
      : path === "/overlay/screenshare"    ? <OverlayScreenShare />
      : path === "/overlay/stinger"        ? <OverlayStinger />
      : path === "/overlay/titlecard"      ? <OverlayTitleCard />
      : path === "/overlay/titlecard-gts"  ? <OverlayTitleCardGTS />
      : path === "/overlay/titlecard-stat" ? <OverlayTitleCardStat />
      : path === "/overlay/titlecard-end"  ? <OverlayTitleCardEnd />
      : path === "/overlay/reference"      ? <OverlayReference />
      : path === "/overlay/outro"          ? <OverlayOutro />
      : path === "/overlay/booth"          ? <OverlayBooth />
      : null;
    if (scene) {
      return (
        <>
          {scene}
          <UnscopedScreenBadge />
        </>
      );
    }
  }

  if (path === "/overlay/main") {
    return <OverlayMain />;
  }

  if (path === "/overlay/alerts") {
    return <OverlayAlerts />;
  }

  if (path === "/overlay/starting-soon") {
    return <OverlayStartingSoon />;
  }

  if (path === "/overlay/break") {
    return <OverlayBreak />;
  }

  if (path === "/overlay/face-cam") {
    return <OverlayFaceCam />;
  }

  if (path === "/overlay/screenshare") {
    return <OverlayScreenShare />;
  }

  if (path === "/overlay/stinger") {
    return <OverlayStinger />;
  }

  if (path === "/overlay/titlecard") {
    return <OverlayTitleCard />;
  }

  if (path === "/overlay/titlecard-gts") {
    return <OverlayTitleCardGTS />;
  }

  if (path === "/overlay/titlecard-stat") {
    return <OverlayTitleCardStat />;
  }

  if (path === "/overlay/titlecard-end") {
    return <OverlayTitleCardEnd />;
  }

  if (path === "/overlay/reference") {
    return <OverlayReference />;
  }

  if (path === "/overlay/outro") {
    return <OverlayOutro />;
  }

  if (path === "/overlay/booth") {
    return <OverlayBooth />;
  }

  if (path === "/join") {
    return <LivestreamJoinView />;
  }

  if (user === undefined) return (
    <div style={{color:"white",background:"#1a0a12",height:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      Loading...
    </div>
  );

  // Identity badge — every staff screen shows who is signed in (bottom-left
  // pill, tap to collapse). Only wraps staff routes; public/overlay routes
  // above never render it.
  const withBadge = (el) => <>{el}<IdentityBadge user={user} roleInfo={roleInfo} /></>;

  // Artist-role accounts are scoped to the Practice Studio: every staff path
  // renders it (public routes already returned above). Firestore rules enforce
  // the same boundary at the data layer, so this is routing, not security.
  if (user && roleInfo?.role === "artist") {
    if (path !== "/practice") {
      window.history.replaceState(null, "", "/practice");
    }
    return withBadge(<PracticeView user={user} navigate={navigate} role="artist" artistName={roleInfo.displayName} artistId={roleInfo.artistId} />);
  }

  // Practice Studio — flash-design training ground (shift-less, own collection).
  if (path === "/practice") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<PracticeView user={user} navigate={navigate} role={roleInfo?.role || "admin"} artistName={roleInfo?.displayName} artistId={roleInfo?.artistId} />);
  }

  if (path === "/register") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<RegisterView user={user} navigate={navigate} />);
  }

  // The pre-2026-09-25 register, intact: ticket batches, printing, expiry,
  // signature line. Kept as a one-tap fallback so a surprise at the booth
  // means switching URLs, not being stuck with a guest in front of you —
  // and as the living record of the thermal flow if booth printing returns.
  if (path === "/register-classic") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<RegisterView user={user} navigate={navigate} classic />);
  }

  if (path === "/queue") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<ArtistQueueView user={user} navigate={navigate} />);
  }

  if (path === "/studio" || path === "/preservation") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<StudioQueue navigate={navigate} />);
  }

  if (path === "/camera") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<CameraView user={user} navigate={navigate} />);
  }

  // Library workspace — unified post-shift curation (Pancakes + Shifts lenses).
  // /gallery and /archive redirect in so existing bookmarks / in-code links still
  // work. Uses history.replaceState (not navigate) to rewrite the URL without
  // dispatching during render.
  if (path === "/library" || path === "/gallery" || path === "/archive") {
    if (!user) return <Login staffOnly={staffOnly} />;
    if (path === "/gallery" && !window.location.search.includes("tab=")) {
      window.history.replaceState(null, "", "/library?tab=pancakes");
    } else if (path === "/archive" && !window.location.search.includes("tab=")) {
      window.history.replaceState(null, "", "/library?tab=shifts");
    }
    return withBadge(<LibraryView user={user} navigate={navigate} />);
  }

  // Social Media Suite — Phase 1 hub (per-shift recap Story share).
  if (path === "/social" || path === "/social/") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<SocialHub navigate={navigate} />);
  }

  if (path === "/insights") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<InsightsView navigate={navigate} />);
  }

  if (path === "/meditations") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<MeditationsView navigate={navigate} />);
  }

  if (path === "/testimonials-mod") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<TestimonialsModeration navigate={navigate} />);
  }

  // Staff-only keepsake preview — renders the full customer keepsake for any
  // order by ID, bypassing tokenId validation. Lets Dan QA the customer
  // experience without printing a real ticket.
  if (path.startsWith("/preview-keepsake/")) {
    if (!user) return <Login staffOnly={staffOnly} />;
    const id = path.slice("/preview-keepsake/".length).split("/")[0];
    return <KeepsakePreview orderId={id} navigate={navigate} />;
  }

  // Public review/testimonial page linked from post-event thank-you emails
  if (path.startsWith("/review/")) {
    const id = path.slice("/review/".length).split("/")[0];
    return <ReviewPage bookingId={id} />;
  }

  // Admin workspace — primary route is /admin. Old /help URLs still work
  // for muscle memory and any unbookmarked share links from prior sessions.
  if (path === "/admin" || path === "/help") {
    return withBadge(<HelpView navigate={navigate} />);
  }

  // Screen Lab — dev dashboard: sandboxed test shift, gate forcing, puppet
  // orders, screen/state catalog. Hard-blocked while a real shift is live.
  if (path === "/lab") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<LabView navigate={navigate} />);
  }

  // Stream Setup — promoted from the old Admin tab to its own top-level since
  // Dan uses it before every stream. Wrapped with minimal header for navigation.
  if (path === "/stream-setup") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", fontFamily: FONT.body }}>
        <div style={{ padding: "0.75rem 1rem", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0 }}>
          <div style={{ color: T.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>📺 Stream Setup</div>
          <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>← Menu</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <StreamSetup />
        </div>
      </div>
    );
  }

  // Event Config — unified per-event setup. Saved profiles stage shift-open
  // settings (config/eventSetup → ShiftManager prefill) and retune overlays
  // (config/stream merge) in one Apply.
  if (path === "/event-config") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", fontFamily: FONT.body }}>
        <div style={{ padding: "0.75rem 1rem", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0 }}>
          <div style={{ color: T.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>🎪 Event Config</div>
          <button onClick={() => navigate("/stream-setup")} style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>📺 Stream Setup</button>
          <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>← Menu</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <EventConfig navigate={navigate} />
        </div>
      </div>
    );
  }

  // Overlay Preview Hub — live thumbnail grid of every /overlay/* scene with
  // pop-out preview + Copy URL. Discoverable home for links that used to be
  // buried in StreamSetup's Scenes section.
  if (path === "/overlay-previews") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", fontFamily: FONT.body }}>
        <div style={{ padding: "0.75rem 1rem", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0 }}>
          <div style={{ color: T.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>🎬 Overlay Previews</div>
          <button onClick={() => navigate("/stream-setup")} style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>📺 Stream Setup</button>
          <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>← Menu</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <OverlayPreviews />
        </div>
      </div>
    );
  }

  // Campaigns — reusable Golden Ticket templates (marketing QRs + pancake gifts)
  if (path === "/campaigns" || path === "/campaigns/") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<CampaignsList navigate={navigate} />);
  }
  if (path === "/campaigns/new") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<CampaignsList navigate={navigate} />);
  }
  if (path.startsWith("/campaigns/")) {
    if (!user) return <Login staffOnly={staffOnly} />;
    const id = path.slice("/campaigns/".length).split("/")[0];
    if (!id) return withBadge(<CampaignsList navigate={navigate} />);
    return withBadge(<CampaignEditor id={id} navigate={navigate} />);
  }

  // Loose ends — the calendar-is-the-boss fixer screen. Reachable without a
  // staff session via the ?k= link key carried by tug-event URLs (they open
  // in Safari, where the home-screen PWA's auth doesn't exist); the callable
  // enforces the key server-side, so rendering the shell unauthenticated is
  // safe.
  if (path === "/fix") {
    const hasKey = new URLSearchParams(window.location.search).has("k");
    if (!user && !hasKey) return <Login staffOnly={staffOnly} />;
    return <FixLooseEnds navigate={user ? navigate : null} />;
  }

  // New Gig quick-add — one-screen hold/gig entry onto the internal-ops
  // calendar. Built to live as an iPhone home-screen shortcut.
  if (path === "/gig" || path === "/gig/") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return <NewGig navigate={navigate} />;
  }

  // Staff calendar — month grid over the bookings collection with day-click
  // quick-add and one-tap Google Calendar import.
  if (path === "/calendar") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<StaffCalendar navigate={navigate} />);
  }

  if (path === "/crm" || path === "/crm/") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<BookingsList navigate={navigate} />);
  }

  if (path === "/crm/add") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<QuickAddFlow navigate={navigate} />);
  }

  if (path === "/crm/add-public-event") {
    if (!user) return <Login staffOnly={staffOnly} />;
    return withBadge(<PublicEventQuickAdd navigate={navigate} />);
  }

  // Event Producer — orate a brief, Claude researches + drafts the full
  // public listing (copy + curated links), Dan reviews, one-tap publish.
  // Reachable via ?k= like /fix so publish tugs can route here from Safari;
  // the callables enforce the key server-side.
  if (path === "/produce") {
    const hasKey = new URLSearchParams(window.location.search).has("k");
    if (!user && !hasKey) return <Login staffOnly={staffOnly} />;
    // withBadge renders nothing for key-only (signed-out) visitors
    return withBadge(<PublicEventQuickAdd navigate={user ? navigate : null} />);
  }

  if (path.startsWith("/crm/clients/")) {
    if (!user) return <Login staffOnly={staffOnly} />;
    const cid = path.slice("/crm/clients/".length).split("/")[0];
    if (!cid) return withBadge(<BookingsList navigate={navigate} />);
    return withBadge(<ClientDetail id={cid} navigate={navigate} />);
  }

  if (path.startsWith("/crm/bookings/")) {
    if (!user) return <Login staffOnly={staffOnly} />;
    const parts = path.slice("/crm/bookings/".length).split("/");
    const id = parts[0];
    const sub = parts[1];
    if (!id) return withBadge(<BookingsList navigate={navigate} />);
    if (sub === "contract") return withBadge(<ContractHelper id={id} navigate={navigate} />);
    return withBadge(<BookingDetail id={id} navigate={navigate} />);
  }

  // Unmatched routes: authenticated users see staff menu, others see login
  if (!user) {
    return <Login staffOnly={staffOnly} />;
  }

  // ── Menu badge — live count of items needing attention ─────────────────
  function MenuBadge({ collectionName, field, value, filterFn }) {
    const [count, setCount] = useState(0);
    useEffect(() => {
      const q2 = query(collection(db, collectionName), where(field, "==", value ?? true));
      return onSnapshot(q2, (snap) => {
        const active = snap.docs.filter(d => {
          const data = d.data();
          if (data.trashedAt) return false;
          if (filterFn && !filterFn(data)) return false;
          return true;
        });
        setCount(active.length);
      });
    }, [collectionName, field, value, filterFn]);
    if (count === 0) return null;
    return <span style={{ background: "rgba(239,83,126,0.9)", color: "white", fontSize: "0.7rem", fontWeight: 800, padding: "0.1rem 0.4rem", borderRadius: "999px", marginLeft: "0.3rem" }}>{count}</span>;
  }

  // Staff menu — accessible at /menu or any unmatched staff path
  // Main menu background — happy-blue radial echoing the /site hero, but
  // deeper and quieter so staff ops read clearly against it.
  const MENU_BG = `radial-gradient(ellipse 140% 110% at 50% 0%,
    ${T.navy} 0%,
    #17387a 25%,
    ${T.bg} 70%,
    #060c20 100%)`;

  return withBadge(
    <div style={{
      padding: "2rem 1.5rem 2.5rem",
      fontFamily: FONT.body,
      background: MENU_BG,
      minHeight: "100vh",
      color: T.text,
    }}>
      <h1 style={{
        color: T.gold,
        fontFamily: FONT.script,
        fontWeight: 700,
        fontSize: "2.25rem",
        lineHeight: 1,
        margin: "0.25rem 0 1.75rem",
        textShadow: "0 4px 24px rgba(252,179,31,0.25)",
      }}>🎟 Golden Ticket</h1>
      {/* Responsive two-column menu: auto-fill grid gives two columns on iPad/
          desktop (where event setup happens) and collapses to the familiar
          single column on iPhone widths. Event Config spans the full grid as
          the first thing staff see on gig morning. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
        gap: "1.25rem",
        alignItems: "start",
        maxWidth: "700px",
      }}>

        <div style={{ gridColumn: "1 / -1" }}>
          <MenuSection label="Event">
            <button onClick={() => navigate("/event-config")} style={btnStyle(T.gold, T.bg)}>
              🎪 Event Config
            </button>
          </MenuSection>
        </div>

        <MenuSection label="Booth">
          <button onClick={() => navigate("/register")} style={btnStyle(T.gold, T.bg)}>
            ⚡ Register
          </button>
          <button onClick={() => navigate("/queue")} style={btnStyle(T.pink, T.text)}>
            🥞 Queue
          </button>
          <button onClick={() => navigate("/camera")} style={btnStyle(T.green, T.text)}>
            📷 Camera
          </button>
        </MenuSection>

        <MenuSection label="Library">
          <button onClick={() => navigate("/library?tab=pancakes")} style={btnStyle(T.blue, T.text)}>
            🖼 Pancakes
          </button>
          <button onClick={() => navigate("/library?tab=shifts")} style={btnStyle(T.orange, T.text)}>
            📋 Shifts
          </button>
          <button onClick={() => navigate("/practice")} style={btnStyle(T.green, T.text)}>
            🎓 Practice <MenuBadge collectionName="practiceReps" field="reviewed" value={false} />
          </button>
          <button onClick={() => navigate("/meditations")} style={btnStyle(T.cream, T.bg)}>
            🧘 Meditations
          </button>
          <button onClick={() => navigate("/testimonials-mod")} style={btnStyle(T.pink, T.text)}>
            💬 Testimonials
          </button>
          <button onClick={() => navigate("/insights")} style={btnStyle(T.sky, T.text)}>
            📊 Insights
          </button>
        </MenuSection>

        <MenuSection label="Social">
          <button onClick={() => navigate("/social")} style={btnStyle(T.gold, T.bg)}>
            📣 Social Studio
          </button>
          <button onClick={() => navigate("/calendar")} style={btnStyle(T.sky, T.text)}>
            📅 Calendar
          </button>
        </MenuSection>

        <MenuSection label="Outreach">
          <button onClick={() => navigate("/gig")} style={btnStyle(T.gold, T.bg)}>
            🥞 New Gig
          </button>
          <button onClick={() => navigate("/fix")} style={btnStyle(T.cream, T.bg)}>
            🧹 Loose Ends
          </button>
          <button onClick={() => navigate("/campaigns")} style={btnStyle(T.gold, T.bg)}>
            🎟 Campaigns
          </button>
          <button onClick={() => navigate("/crm")} style={btnStyle(T.pink, T.text)}>
            📇 Bookings
          </button>
          <button onClick={() => navigate("/shop/orders")} style={btnStyle(T.orange, T.text)}>
            📦 Shop Orders
          </button>
          <button onClick={() => navigate("/preservation")} style={btnStyle(T.cream, T.bg)}>
            🖌 Preservation <MenuBadge collectionName="orders" field="isPreservation" filterFn={d => d.preservationStatus && d.preservationStatus !== "shipped"} />
          </button>
          <button onClick={() => navigate("/")} style={btnStyle(T.sky, T.text)}>
            🌐 Public Site
          </button>
        </MenuSection>

        <MenuSection label="Stream">
          <button onClick={() => navigate("/stream-setup")} style={btnStyle(T.blue, T.text)}>
            📺 Stream Setup
          </button>
          <button onClick={() => navigate("/overlay-previews")} style={btnStyle(T.sky, T.text)}>
            🎬 Overlay Previews
          </button>
        </MenuSection>

        <MenuSection label="Admin">
          <button onClick={() => navigate("/admin")} style={btnStyle(T.cream, T.bg)}>
            🛠 Admin
          </button>
          <button onClick={() => navigate("/lab")} style={btnStyle(T.sky, T.text)}>
            🧪 Screen Lab
          </button>
        </MenuSection>

        <div style={{ marginTop: "0.5rem", gridColumn: "1 / -1" }}>
          <button onClick={() => auth.signOut()} style={btnStyle(T.raised, T.muted)}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// Menu section wrapper — groups related top-level views under a muted heading
// so the main menu reads as organized workspaces instead of a flat button stack.
function MenuSection({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <div style={{
        color: "rgba(255,218,173,0.55)",
        fontFamily: FONT.display,
        fontSize: "0.72rem",
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        paddingLeft: "0.25rem",
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function btnStyle(bg, color) {
  return {
    background: bg,
    color,
    border: "none",
    borderRadius: "12px",
    padding: "0.95rem 1.4rem",
    fontSize: "1rem",
    fontWeight: 700,
    fontFamily: FONT.display,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  };
}

export default App;